import config from '@/common-helpers/web3modalconfig';
import { RPC } from '@/constant';
import user from '@/stores/user';
import { getGasPrice as getGasPriceWagmi } from '@wagmi/core';
import BigNumber from 'bignumber.js';
import { Contract } from 'ethers';
import { useEffect, useMemo, useState } from 'react';
import { useChainId } from 'wagmi';
import Web3 from 'web3';

function decodeErrorString(error: any) {
    if (!error) return 'Unknown error';

    let errorMessage = '';

    // Handle string type errors
    if (typeof error === 'string') {
        error = new Error(error).message;
    } else if (error instanceof Error) {
        error = error.message;
    } else if (typeof error === 'object') {
        // Try to use possible error message fields in the object
        error =
            error.message ||
            error.reason ||
            error.error ||
            JSON.stringify(error);
    }

    if (error) {
        const reg = new RegExp('Error: Internal JSON-RPC error.');
        if (reg.test(error)) {
            try {
                const message = error.replace(reg, '');
                const errorData = JSON.parse(message);

                if (
                    errorData &&
                    typeof errorData === 'object' &&
                    errorData.message
                ) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
                // JSON parsing failed, use original error message
                errorMessage = error;
            }
        } else {
            errorMessage = error;
        }
    }

    // Make common error messages more user-friendly
    if (errorMessage.includes('insufficient funds')) {
        return 'Insufficient balance';
    } else if (errorMessage.includes('gas required exceeds allowance')) {
        return 'Transaction gas required exceeds wallet balance';
    } else if (errorMessage.includes('execution reverted')) {
        return (
            'Transaction execution reverted: ' +
            (errorMessage.split('execution reverted:')[1]?.trim() ||
                'Unknown reason')
        );
    }

    return errorMessage || 'Unknown error';
}

export function useGetGasPrice() {
    const [gasPrice, setGasPrice] = useState(0);
    const chainId = useChainId();
    async function getGasPrice() {
        const gasPrice = await getGasPriceWagmi(config, {
            chainId: chainId,
        });
        setGasPrice(Number(gasPrice));
    }

    useEffect(() => {
        getGasPrice();
    }, [chainId]);
    return gasPrice;
}

export function useEtherGasEstimate() {
    async function gasEstimate(
        contract: Contract,
        method: string,
        params: any,
        sendParams?: { value: string },
    ): Promise<{
        success: boolean;
        value?: number;
        errorMessage?: string;
    }> {
        try {
            // let gasPrice = 1000000000
            // if (library?.provider) {
            //   gasPrice = await (library.provider as any).getGasPrice()
            //   console.log(gasPrice)
            // }

            // Create estimateGas params object, adding value if provided

            // For estimating gas, we need to combine the transaction parameters with options
            let estimation;

            if (sendParams) {
                estimation = await contract[method].estimateGas(...params, {
                    value: sendParams.value,
                });
            } else {
                estimation = await contract[method].estimateGas(...params);
            }

            console.log('contract', contract);
            console.log('method', method);
            console.log('params', params);
            console.log('sendParams', sendParams);
            console.log('estimation', estimation.toString());
            return {
                success: true,
                value: Number(estimation),
            };
        } catch (e: any) {
            console.error('Gas estimation error:', JSON.stringify(e, null, 2));

            let errorMessage = '';

            // Try to extract useful information from different error formats
            if (e?.error?.shortMessage || e?.error?.details) {
                const shortMsg = e?.error?.shortMessage || '';
                const details = e?.error?.details || '';
                errorMessage =
                    shortMsg + (shortMsg && details ? ': ' : '') + details;
            } else if (e?.data?.message) {
                // Handle other possible error formats
                errorMessage = e.data.message;
            } else if (e?.message) {
                // Standard Error object
                errorMessage = e.message;
            } else if (typeof e === 'string') {
                // String error
                errorMessage = e;
            } else {
                // Fallback case
                errorMessage = 'Unknown error';
            }

            // Make common error messages more user-friendly
            if (errorMessage.includes('gas required exceeds allowance')) {
                errorMessage =
                    'Transaction gas required exceeds wallet balance';
            } else if (errorMessage.includes('execution reverted')) {
                errorMessage =
                    'Transaction execution reverted: ' +
                    (errorMessage.split('execution reverted:')[1]?.trim() ||
                        'Unknown reason');
            }

            return {
                success: false,
                errorMessage,
            };
        }
    }

    return gasEstimate;
}

export function useGetEstimatedGas({
    contract,
    method,
    params,
    show = false,
    fetch = false,
}: {
    contract: Contract | null;
    method: string;
    params: any;
    show?: boolean;
    fetch?: boolean;
}) {
    const [estimatedGas, setEstimatedGas] = useState('0');
    const [error, setError] = useState<string | null>(null);

    const web3 = useMemo(() => {
        return new Web3(new Web3.providers.HttpProvider(RPC[user.chainId]));
    }, [user.chainId]);

    async function getGas() {
        try {
            if (!contract) {
                setError('Contract is not available');
                return;
            }

            if (!user.connected) {
                setError('Wallet not connected');
                return;
            }

            if (!contract[method]) {
                setError(`Method ${method} not found on contract`);
                return;
            }

            const estimation = await contract[method].estimateGas(...params);

            if (!estimation) {
                setError('Failed to estimate gas');
                return;
            }

            const gasPrice = await web3?.eth.getGasPrice();

            if (!gasPrice) {
                setError('Failed to get gas price');
                return;
            }

            setEstimatedGas(
                new BigNumber(estimation?.toString())
                    .shiftedBy(-18)
                    .multipliedBy(gasPrice || 1)
                    .toFixed(),
            );

            setError(null);
        } catch (e: any) {
            console.error('Gas estimation error:', e);
            setError(e?.message || 'Unknown error estimating gas');
            setEstimatedGas('0');
        }
    }

    useEffect(() => {
        if (!fetch) return;
        setError(null);
        getGas();
    }, [contract, method, params, user.connected, fetch, web3]);

    return {
        estimatedGas: show ? estimatedGas : '0',
        error,
    };
}
